import React, { useState } from 'react';

import { c } from 'ttag';

import { Badge, Checkbox, CollapsibleSection, Table, TableBody, TableCell, TableRow } from '@freelancelabs/components';

import { AccountActivitiesQuery } from '../../../graphql/__generated__/types';

type Activities = NonNullable<AccountActivitiesQuery['accountActivities']>;
type Option = Activities['needs'][number];

interface Props {
    activities: Activities;
    needIds: string[];
    serviceIds: string[];
    onToggleNeed: (id: string, checked: boolean) => void;
    onToggleService: (id: string, checked: boolean) => void;
    /** Coche ou décoche toutes les prestations d'un marché d'un seul geste. */
    onToggleMarket: (serviceIds: string[], checked: boolean) => void;
}

/**
 * LE SÉLECTEUR D'ACTIVITÉS de l'étape « Périmètre » (PCG-305).
 *
 * **On cochait des marchés.** La branche restreinte des vues joignait alors sur le marché, et
 * une activité qui n'en porte aucun ne correspondait à rien : elle sortait de la liste **en
 * silence**, 16 % des besoins et 27 % des prestations. On coche désormais les objets
 * eux-mêmes, et le refus qui protégeait de ce silence a disparu avec sa cause.
 *
 * **Attention, LE MARCHÉ N'EST QU'UNE CASE GROUPÉE.** Il regroupe et il coche en bloc, mais ce
 * que l'écran envoie est la liste des PRESTATIONS. Envoyer le marché ramènerait exactement le
 * défaut que ce lot corrige. C'est le dessin de la maquette du back-office, relevé dans son
 * code : sa case de cadre écrit dans `contractIds`, jamais dans une liste de cadres.
 *
 * **Le groupe « sans contrat cadre » se rend comme les autres**, rangé en dernier par l'API.
 * C'est près d'une prestation sur deux sur certains comptes, et c'est exactement ce que
 * l'ancien mécanisme perdait : l'écarter serait refaire le silence d'avant.
 *
 * ── LA FORME EST L'ACCORDÉON DE L'ÉCRAN DES ACTIVITÉS DU COMPTE
 *
 * Un accordéon par groupe (« Besoins », puis chaque marché), l'intitulé nomme le groupe, le
 * compteur dit ce qui est coché sur le total, la table liste les activités cochables. C'est le
 * même composant que la fiche du compte et la fiche utilisateur : le portail n'a pas deux idées
 * du chevron, du repli et de l'anneau de focus.
 *
 * **Rien d'interactif dans l'en-tête d'un `<details>` : la case « tout le marché » vit DANS le
 * panneau**, au-dessus de la table. Posée dans le `<summary>`, elle cocherait le marché ET
 * replierait la section du même clic.
 *
 * **Les sections sont repliées au départ**, comme l'écran des comptes : l'état d'ouverture est
 * tenu par un ensemble des sections OUVERTES, vide au premier rendu. Le compteur de l'en-tête
 * dit ce qui est coché sans qu'on ait à déplier.
 *
 * **Chaque panneau défile dans lui-même** (`max-h`), la borne de la maquette : un compte peut
 * porter 741 besoins, et sans elle la section pousse les suivantes et les boutons de
 * l'assistant à mille lignes plus bas.
 *
 * ── CE QUI N'EST PLUS ICI, ET POURQUOI
 *
 * **Le champ de recherche et le compteur global ont quitté ce composant.** Ils y étaient, donc
 * ils n'existaient que tant qu'une liste existait : une requête en vol rend `data` indéfini,
 * `AttachmentRow` cessait de rendre ce composant, et le champ disparaissait A CHAQUE TOUCHE avec
 * son contenu et le curseur. On tapait quatre lettres, il en restait deux.
 *
 * La règle : **un contrôle qui filtre ne se monte jamais sous ce qu'il filtre.** Ce composant ne
 * rend que les listes, et il peut disparaître sans emporter la recherche.
 */
const ActivityPicker = ({ activities, needIds, serviceIds, onToggleNeed, onToggleService, onToggleMarket }: Props) => {
    const checkedNeeds = new Set(needIds);
    const checkedServices = new Set(serviceIds);

    // Les sections OUVERTES : ensemble vide au départ, donc tout est replié, comme l'écran des
    // comptes. Le compteur de l'en-tête porte déjà ce qui est coché, sans avoir à déplier.
    const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());
    const isOpen = (key: string) => openKeys.has(key);
    const toggleOpen = (key: string, open: boolean) =>
        setOpenKeys((prev) => {
            const next = new Set(prev);
            if (open) {
                next.add(key);
            } else {
                next.delete(key);
            }
            return next;
        });

    /**
     * Le compteur d'une section : le total, ou « coché / total » quand la sélection est
     * partielle. Une section repliée dit ainsi d'un coup d'œil ce qu'elle porte de coché.
     */
    const countLabel = (checkedCount: number, total: number) =>
        checkedCount > 0 && checkedCount < total ? `${checkedCount}/${total}` : String(total);

    /**
     * Une ligne cochable de la table.
     *
     * Le nom peut être nul, et il s'écrit alors : une case sans libellé se lit comme une ligne
     * cassée, et l'opérateur ne saurait pas ce qu'il coche. Le badge « désigné par la source »
     * est sur la LIGNE : une case cochée sans raison à côté est un périmètre accordé par
     * personne. Le statut part à droite, dans sa propre cellule.
     */
    const row = (option: Option, checked: boolean, toggle: (id: string, checked: boolean) => void) => (
        <TableRow key={option.id}>
            <TableCell>
                <Checkbox
                    id={`activity-${option.id}`}
                    checked={checked}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        toggle(option.id as string, event.target.checked)
                    }
                >
                    <span className="flex flex-wrap items-center gap-2">
                        <span>{option.name ?? c('Onboarding activity').t`Unnamed`}</span>
                        {option.linkedToContact && (
                            <Badge category="info" size="small">
                                {c('Onboarding activity').t`Named by the source`}
                            </Badge>
                        )}
                    </span>
                </Checkbox>
            </TableCell>
            <TableCell className="text-end align-middle">
                {option.displayStatus && <span className="text-xs text-text-600">{option.displayStatus}</span>}
            </TableCell>
        </TableRow>
    );

    const checkedNeedCount = activities.needs.filter((n) => checkedNeeds.has(n.id as string)).length;

    return (
        <div className="flex flex-col gap-2">
            {/*
                Ce que le plafond a laissé dehors, dit en toutes lettres. Un sélecteur tronqué en
                silence fait croire que le compte n'a que ce qu'on voit, et l'opérateur coche
                alors un périmètre qu'il croit complet.
            */}
            {(activities.omittedNeeds > 0 || activities.omittedServices > 0) && (
                <p className="text-xs text-danger-700" role="alert">
                    {c('Onboarding activity')
                        .t`This account carries more activities than this list shows. Narrow the search to reach the ones missing.`}
                </p>
            )}

            {/* LES BESOINS, groupe unique. Toujours rendu, même vide : « aucun besoin ne
                correspond » est un fait de la recherche que l'opérateur doit voir. */}
            <CollapsibleSection
                className="fl-collapsible-section--group fl-collapsible-section--white-head"
                open={isOpen('needs')}
                onOpenChange={(open) => toggleOpen('needs', open)}
                title={c('Onboarding activity').t`Needs`}
                count={
                    <Badge category="default" size="small">
                        {countLabel(checkedNeedCount, activities.needs.length)}
                    </Badge>
                }
            >
                {activities.needs.length === 0 ? (
                    <p className="px-2.5 py-2 text-xs text-text-600">{c('Onboarding activity').t`No need matches.`}</p>
                ) : (
                    <div className="max-h-[340px] overflow-auto">
                        <Table>
                            <TableBody>
                                {activities.needs.map((option) =>
                                    row(option, checkedNeeds.has(option.id as string), onToggleNeed)
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </CollapsibleSection>

            {/* LES PRESTATIONS, un accordéon par marché. « Sans contrat cadre » est rangé en
                dernier par l'API et se rend comme les autres. */}
            {activities.marketGroups.length === 0 ? (
                <p className="px-2.5 py-2 text-xs text-text-600">{c('Onboarding activity').t`No service matches.`}</p>
            ) : (
                activities.marketGroups.map((group) => {
                    const key = group.marketId ?? 'no-market';
                    const ids = group.services.map((s) => s.id as string);
                    const checkedCount = ids.filter((id) => checkedServices.has(id)).length;
                    const allChecked = ids.length > 0 && checkedCount === ids.length;
                    // Le marché sans nom et le SANS marché sont deux phrases différentes : la
                    // première dit qu'un cadre existe et n'a pas d'intitulé, la seconde qu'il n'y
                    // en a aucun. Les confondre accuserait le référentiel d'un trou qu'il n'a pas.
                    const label =
                        group.marketId === null
                            ? c('Onboarding activity').t`Without a framework agreement`
                            : (group.marketName ?? c('Onboarding activity').t`Market without a name`);
                    return (
                        <CollapsibleSection
                            key={key}
                            className="fl-collapsible-section--group fl-collapsible-section--white-head"
                            open={isOpen(key)}
                            onOpenChange={(open) => toggleOpen(key, open)}
                            title={label}
                            count={
                                <Badge category="default" size="small">
                                    {countLabel(checkedCount, ids.length)}
                                </Badge>
                            }
                        >
                            {/* LA CASE « TOUT LE MARCHÉ » vit dans le panneau, jamais dans
                                l'en-tête d'un `<details>` où le clic replierait la section. Elle
                                ne coche que ce qui est SOUS ELLE : sous une recherche, le groupe
                                ne porte que les prestations retenues. */}
                            <div className="border-b border-border-norm px-2.5 py-2">
                                <Checkbox
                                    id={`market-${key}`}
                                    checked={allChecked}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                        onToggleMarket(ids, event.target.checked)
                                    }
                                >
                                    <span className="text-xs font-medium text-text-700">
                                        {c('Onboarding activity').t`Select every service shown here`}
                                    </span>
                                </Checkbox>
                            </div>
                            <div className="max-h-[340px] overflow-auto">
                                <Table>
                                    <TableBody>
                                        {group.services.map((option) =>
                                            row(option, checkedServices.has(option.id as string), onToggleService)
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CollapsibleSection>
                    );
                })
            )}
        </div>
    );
};

export default ActivityPicker;
